import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import careersImg from '../media/generated/careers-hero.webp';
import { Section, SectionHeading, PageHero, Card, IconBadge, Steps, Faq, fadeUp, EmbeddedForm } from '../components/ui';
import { BrainIcon, SparkleIcon, PuzzleIcon, BriefcaseIcon, BookIcon, ChatIcon } from '../components/icons';

// TODO: replace with the real Google Form embed link for job applications
// (Google Forms → Send → embed <> icon → copy the src URL).
const CAREERS_FORM_URL = 'https://docs.google.com/forms/d/e/REPLACE_WITH_CAREERS_FORM_ID/viewform?embedded=true';
const IS_FORM_CONFIGURED = !CAREERS_FORM_URL.includes('REPLACE_WITH');

const WhyJoin = () => (
  <Section>
    <SectionHeading
      eyebrow="Why join us"
      title="Small team, real scope, work that ships"
      subtitle="We're early enough that what you build reaches students in weeks, not quarters."
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
      {[
        {
          icon: BriefcaseIcon,
          title: 'Own real surface area',
          desc: "You won't be handed tickets. You'll own a product area end to end — from the problem framing to what students actually experience.",
        },
        {
          icon: BrainIcon,
          title: 'Work on hard AI problems',
          desc: 'Personalization that persists across sessions, learner modelling, and evaluation of explanations are genuinely unsolved. That is the work.',
        },
        {
          icon: BookIcon,
          title: 'See the impact directly',
          desc: 'Our users are students and teachers. Feedback loops are short, honest, and occasionally humbling.',
        },
        {
          icon: SparkleIcon,
          title: 'Backed and building',
          desc: 'Incubated at SIIC, IIT Kanpur, with the mentorship and infrastructure that comes with it — while staying a small, fast team.',
        },
      ].map((item) => (
        <Card key={item.title} hover>
          <IconBadge icon={item.icon} className="mb-4" />
          <h3 className="font-bold text-white mb-2">{item.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
        </Card>
      ))}
    </div>
  </Section>
);

const WhatWeLookFor = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="What we look for" title="People who care about learning, not just AI" />
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
      {[
        { icon: BrainIcon, title: 'Curious builders', desc: 'You like understanding a problem deeply before shipping a solution to it.' },
        { icon: SparkleIcon, title: 'Comfortable with AI', desc: "You use AI tools daily and have opinions on where they help students and where they don't." },
        { icon: PuzzleIcon, title: 'Ownership mindset', desc: "Small team, real scope — you'll own outcomes, not just tasks." },
      ].map((item) => (
        <motion.div key={item.title} {...fadeUp} className="p-6 rounded-2xl bg-white/[0.04] border border-white/10 shadow-sm text-center">
          <IconBadge icon={item.icon} className="mx-auto mb-4" />
          <h3 className="font-bold text-white mb-2">{item.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
        </motion.div>
      ))}
    </div>
  </Section>
);

const Process = () => (
  <Section>
    <SectionHeading
      eyebrow="Hiring process"
      title="How we hire"
      subtitle="Short, respectful of your time, and focused on real work rather than puzzles."
    />
    <div className="max-w-4xl mx-auto">
      <Steps
        steps={[
          { title: 'Send your details', desc: 'Fill in the form below with your background and what you want to work on.' },
          { title: 'Intro conversation', desc: "A call to understand what you're good at, what you want next, and whether the scope fits." },
          { title: 'Work session', desc: 'A practical problem close to what you would actually do here — discussed together, not graded in isolation.' },
        ]}
      />
    </div>
  </Section>
);

const OpenRoles = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="Open roles" title="What we're hiring for" />
    <motion.div {...fadeUp} className="max-w-2xl mx-auto">
      <div className="flex items-start gap-4 p-6 sm:p-8 rounded-2xl bg-white/[0.04] border border-white/10 shadow-sm">
        <IconBadge icon={BriefcaseIcon} className="flex-shrink-0" />
        <div>
          <h3 className="font-bold text-white mb-2">No specific roles listed right now</h3>
          <p className="text-sm text-gray-400 leading-relaxed">
            We hire in small bursts as Gyanbeej and GyanSpark grow, and we're always interested in strong
            engineers, designers, and educators. Send us your details below — we review every application and
            reach out when there's a fit.
          </p>
        </div>
      </div>
    </motion.div>
  </Section>
);

const ApplicationForm = () => (
  <Section>
    <motion.div
      {...fadeUp}
      className="max-w-3xl mx-auto bg-gradient-to-br from-[#1a0033] to-[#3b0764] text-white p-6 md:p-12 rounded-3xl shadow-2xl"
    >
      <div className="text-center mb-8">
        <span className="text-sm font-bold tracking-widest uppercase text-purple-300">Apply</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3">Tell us about yourself</h2>
        <p className="text-purple-100 mt-4 max-w-md mx-auto">
          Share your background and what you'd want to build here. We read everything that comes in.
        </p>
      </div>
      <div className="max-w-md mx-auto">
        {IS_FORM_CONFIGURED ? (
          <>
            <p className="text-center text-sm text-purple-100/70 mb-3">
              Just keep scrolling the page — the form and its Submit button are below.
            </p>
            <EmbeddedForm src={CAREERS_FORM_URL} height={1800} title="Careers Application Form" />
          </>
        ) : (
          <div className="rounded-xl bg-white/10 border border-white/20 p-8 text-center">
            <IconBadge icon={ChatIcon} className="mx-auto mb-4 bg-white/15 text-purple-200" />
            <p className="text-purple-100 mb-6">
              Our application form is being set up. In the meantime, reach us through the contact page and
              we'll get straight back to you.
            </p>
            <Link
              to="/contact"
              className="inline-block px-6 py-3 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors"
            >
              Contact us
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  </Section>
);

const CareersFaq = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="FAQ" title="Working at Preative" />
    <Faq
      items={[
        {
          q: 'Do you hire interns or freshers?',
          a: 'Yes. We care more about how you think and what you have built than about years of experience. If you are early in your career and can show us something you made, apply.',
        },
        {
          q: 'Is the role remote or in person?',
          a: 'We are a small India-based team incubated at SIIC, IIT Kanpur. Working arrangements are discussed case by case depending on the role.',
        },
        {
          q: 'What does the team work on day to day?',
          a: 'Two live products: Gyanbeej, our AI learning assistant for NCERT students, and GyanSpark, our personalization layer for EdTech platforms. Work spans product engineering, AI systems, design, and content.',
        },
        {
          q: 'What if there is no open role that matches me?',
          a: 'Send your details anyway. We keep applications on file and reach out when a role opens that fits what you are good at.',
        },
      ]}
    />
  </Section>
);

const Careers = () => (
  <>
    <Seo
      title="Careers"
      description="Join Preative Solutions and help build AI-native education products. We're a small team building Gyanbeej and GyanSpark, incubated at SIIC IIT Kanpur."
      path="/careers"
    />
    <PageHero
      eyebrow="Careers"
      title="Build the future of learning with us"
      subtitle="We're a small team working on AI and education. If that's the problem you want to spend your time on, we want to hear from you."
      image={careersImg}
      imageAlt="Illustration of building blocks assembling upward"
    />
    <WhyJoin />
    <WhatWeLookFor />
    <Process />
    <OpenRoles />
    <ApplicationForm />
    <CareersFaq />
  </>
);

export default Careers;
